// ─── OpenRouter/Anthropic API cost (USD per token) ────────
// Reference rates from Credit & Cost Policy v1.0 (21 เม.ย. 2569)
export const MODEL_API_COST_USD: Record<string, { input: number; output: number }> = {
  'google/gemini-2.5-flash':               { input: 0.0000003,  output: 0.0000025  },
  'google/gemini-3-flash-preview':         { input: 0.0000005,  output: 0.000003   },
  'google/gemini-3.1-flash-image-preview': { input: 0.000002,   output: 0.000012   },
  'anthropic/claude-sonnet-4.5':           { input: 0.000003,   output: 0.000015   },
  'anthropic/claude-sonnet-4.6':           { input: 0.000003,   output: 0.000015   },
  'anthropic/claude-sonnet-5.5':           { input: 0.000002,   output: 0.00001    },
  'anthropic/claude-opus-4.6':             { input: 0.000015,   output: 0.000075   },
  'openai/gpt-4o':                         { input: 0.0000025,  output: 0.00001    },
  'openai/gpt-5.3-chat':                   { input: 0.000005,   output: 0.00002    },
  'openai/o3':                             { input: 0.00001,    output: 0.00004    },
  'x-ai/grok-4-1-fast':                   { input: 0.000003,   output: 0.000009   },
  'x-ai/grok-4.20':                       { input: 0.00000125, output: 0.0000025  },
  'google/veo-3.1-lite':                   { input: 0.0,        output: 0.0        },
  'google/veo-3.1-fast':                   { input: 0.0,        output: 0.0        },
  'google/veo-3.1':                        { input: 0.0,        output: 0.0        },
}

// ─── Platform config ──────────────────────────────────────
// 1 AIM Credit = ฿1 reference value (Policy v1.0)
export const CREDIT_CONFIG = {
  baht_per_credit: 1,
  usd_to_baht: 35,
} as const

// ─── Helper: estimate actual API cost in USD ──────────────
export function estimateApiCostUsd(
  model: string,
  inputTokens: number,
  outputTokens: number
): number {
  const rate = MODEL_API_COST_USD[model]
  if (!rate) return (inputTokens + outputTokens) * 0.000002
  return inputTokens * rate.input + outputTokens * rate.output
}

// ─── Recipe credit cost per generation (by routed model) ──
export const RECIPE_CREDIT_COST: Record<string, number> = {
  'google/gemini-2.5-flash':               1,
  'google/gemini-3-flash-preview':         1,
  'google/gemini-3.1-flash-image-preview': 3,
  'anthropic/claude-sonnet-4.5':           2,
  'anthropic/claude-sonnet-4.6':           2,
  'anthropic/claude-sonnet-5.5':           2,
  'openai/gpt-4o':                         2,
  'x-ai/grok-4-1-fast':                   2,
  'x-ai/grok-4.20':                       2,
  'openai/gpt-5.3-chat':                   3,
  'anthropic/claude-opus-4.6':             4,
  'openai/o3':                             5,
  'google/veo-3.1-lite':                   15,
  'google/veo-3.1-fast':                   45,
  'google/veo-3.1':                        120,
  '__default__':                           2,
}

export function getRecipeCreditCost(routedModel: string): number {
  return RECIPE_CREDIT_COST[routedModel] ?? RECIPE_CREDIT_COST['__default__']
}

// ─── Room/Workshop credit cost (per session) ─────────────
// Policy v1.0: S=≤30, M=≤100, L=≤100 (Expert only)
export const ROOM_CREDIT_COST = {
  size_s: 5,   // ≤30 participants  — Power+
  size_m: 12,  // ≤100 participants — Power+
  size_l: 40,  // ≤100 participants — Expert only
} as const

export function getRoomCreditCost(maxParticipants: number): number {
  if (maxParticipants <= 30)  return ROOM_CREDIT_COST.size_s
  if (maxParticipants <= 100) return ROOM_CREDIT_COST.size_m
  return ROOM_CREDIT_COST.size_l
}

// ─── Tier quota + pricing ─────────────────────────────────
// Policy v1.0: free / beginner / power / expert
// daily_credits = reset ทุกเที่ยงคืน (use-or-lose)
// monthly_credits = base allocation ต่อ billing cycle
export const TIER_QUOTA = {
  free: {
    monthly_credits: 0,
    daily_credits: 3,
    monthly_rooms: 1,
    max_participants: 5,
    price_thb: 0,
    price_thb_annual: 0,
  },
  beginner: {
    monthly_credits: 150,
    daily_credits: 3,
    monthly_rooms: 5,
    max_participants: 30,
    price_thb: 190,
    price_thb_annual: 1900,
  },
  power: {
    monthly_credits: 360,
    daily_credits: 5,
    monthly_rooms: 15,
    max_participants: 100,
    price_thb: 390,
    price_thb_annual: 3900,
  },
  expert: {
    monthly_credits: 750,
    daily_credits: 10,
    monthly_rooms: 30,
    max_participants: 100,
    price_thb: 750,
    price_thb_annual: 7500,
  },
} as const

export type UserTier = keyof typeof TIER_QUOTA

export function getTierQuota(tier: string) {
  // backward compat: map old names → new
  const mapped = tier === 'pro' ? 'beginner' : tier === 'team' ? 'expert' : tier
  return TIER_QUOTA[mapped as UserTier] ?? TIER_QUOTA.free
}

// ─── Asset gate helper ────────────────────────────────────
export function getAssetGateStatus(
  difficulty: string,
  userTier: string
): 'allowed' | 'upgrade_beginner' | 'upgrade_power' | 'login_required' {
  if (difficulty === 'beginner') return 'allowed'
  const mappedTier = userTier === 'pro' ? 'beginner' : userTier === 'team' ? 'expert' : userTier
  const subTiers = ['beginner', 'power', 'expert']
  const powerTiers = ['power', 'expert']
  if (difficulty === 'intermediate') {
    return subTiers.includes(mappedTier) ? 'allowed' : 'upgrade_beginner'
  }
  if (difficulty === 'expert') {
    return powerTiers.includes(mappedTier) ? 'allowed' : 'upgrade_power'
  }
  return 'allowed'
}

// ─── Top-up packages ──────────────────────────────────────
// Policy v1.0: top-up เพิ่ม credits เท่านั้น — ไม่ unlock features
// NOTE: Top-up credits DO NOT expire (per Policy v1.0 update)
export const TOPUP_PACKAGES = [
  { id: 'tier1', credits: 25,  price_thb: 25,  label: 'Starter',  useCase: 'เจนภาพ 8 ภาพ · Recipe 25 ครั้ง' },
  { id: 'tier2', credits: 110, price_thb: 95,  label: 'Standard', useCase: 'เจนภาพ 36 ภาพ · Video Lite 7 คลิป', badge: 'POPULAR' },
  { id: 'tier3', credits: 180, price_thb: 149, label: 'Pro Pack', useCase: 'เจนภาพ 60 ภาพ · Video Lite 12 คลิป', badge: 'BEST VALUE' },
] as const

